import React, { useCallback, useEffect } from 'react';

import { Arrow90degLeft, PencilSquare } from 'react-bootstrap-icons';
import { allowedImageRegex, convertBytesToMB } from '../utils';
import {
  Button,
  Form,
  Image,
  OverlayTrigger,
  Table,
  Tooltip
} from 'react-bootstrap';
import { CommonModal } from '../../../../common/modals/common-modal';
import { CopyToClipboardButton } from '../../../../common/copyable/copy-to-clipboard-button/copy-to-clipboard-button';
import { UiIcon } from '../../../../common/icons/ui-icon';
import { UploadImageButton } from './upload-image-button';
import { WaitSpinner } from '../../../../common/wait-spinner/wait-spinner';
import { imageHelperApi } from '../../../../../services/imageHelper';
import { useUiNotifications } from '../../../../notifications/ui-notification-boundary';
import { Logger } from '../../../../../utils/logger';

/**
 * Shows a modal that allows the user to update the image.
 */
export type ImageLog = {
  action: string;
  time: string;
  user: string;
};

export type ImageFile = {
  id: string;
  name: string;
  url: string;
  size: string;
  tags: any[];
  logs: ImageLog[];
  dimension: string;
};

const logger = new Logger('Edit image modal');

type EditImageModalProps = {
  showEditImageModal: boolean;
  handleEditClose: (show: boolean) => void;
  uploadImage: File | undefined;
  setUploadImage: (file: File | undefined) => void;
  imageFile: ImageFile;
  isLoading: boolean;
};

const EditImageModal = ({
  showEditImageModal,
  handleEditClose,
  uploadImage,
  setUploadImage,
  imageFile,
  isLoading
}: EditImageModalProps) => {
  const [
    updateImageDataTrigger,
    { isLoading: updateImageLoader, status, error }
  ] = imageHelperApi.endpoints.updateImageData.useMutation();
  const [imageLibraryTrigger] =
    imageHelperApi.endpoints.getImageLibrary.useLazyQuery();
  const { dispatchUiNotification } = useUiNotifications();

  useEffect(() => {
    if (status === 'uninitialized') return;
    if (status === 'fulfilled') {
      handleEditClose(false);
      imageLibraryTrigger({ currentPage: 1, search: '', perPage: 20 });
    }
    if (status === 'rejected') {
      dispatchUiNotification('Update Image Failed', error?.data?.error, {});
    }
  }, [status, error, dispatchUiNotification]);

  const handleSaveImage = useCallback(async () => {
    if (uploadImage) {
      const imageData = new FormData();
      imageData.append('image', uploadImage);
      imageData.append('filename', imageFile.name);
      imageData.append('tags', JSON.stringify(imageFile.tags ?? []));
      updateImageDataTrigger({
        id: imageFile.id,
        originalObject: imageData
      });
    }
  }, [uploadImage, imageFile]);

  const clipboardUI = (text) => (
    <CopyToClipboardButton content={text!} size='sm' variant='rounded' />
  );

  return (
    <>
      <CommonModal
        show={showEditImageModal}
        onHide={() => handleEditClose(false)}
        title='Edit Image'
        modalSize='lg'
        centered
        showCloseButton>
        <div>
          {isLoading ? (
            <WaitSpinner size='30rem' />
          ) : (
            <>
              <div className='p-5 pb-0 d-flex' style={{ gap: '2rem' }}>
                <div
                  className='d-flex flex-column w-50'
                  style={{ gap: '1rem' }}>
                  <div className='d-flex flex-row justify-content-between'>
                    <div>
                      {uploadImage ? (
                        <Button
                          title='Cancel upload'
                          size='lg'
                          variant='rounded'
                          onClick={() => setUploadImage(undefined)}>
                          <UiIcon size='20' icon={Arrow90degLeft} />
                        </Button>
                      ) : null}
                    </div>
                    <UploadImageButton
                      supportedFormats={'image/webp'}
                      onUploadImage={(file: File | undefined) => {
                        const fileExtension = file?.name.split('.').pop();
                        if (!fileExtension) {
                          dispatchUiNotification(
                            'Invalid File Format',
                            'Only webp files are allowed',
                            {}
                          );
                          return;
                        }
                        if (!allowedImageRegex.test(fileExtension)) {
                          dispatchUiNotification(
                            'Invalid File Format',
                            'Only webp files are allowed',
                            {}
                          );
                          return;
                        }
                        setUploadImage(file);
                      }}
                      icon={PencilSquare}
                      variant='rounded'
                      size='20'
                    />
                  </div>
                  <div className='d-flex justify-content-center align-items-center'>
                    <Image
                      fluid
                      src={
                        uploadImage
                          ? URL.createObjectURL(uploadImage)
                          : imageFile?.url
                      }
                      alt={imageFile?.name}
                      style={{ objectFit: 'scale-down', height: '50rem' }}
                    />
                  </div>
                </div>
                <div
                  className='w-50 d-flex flex-column overflow-auto'
                  style={{ gap: '2rem' }}>
                  <Form>
                    <Form.Group className='mb-3'>
                      <Form.Label>File Name</Form.Label>
                      <div className='d-flex'>
                        <Form.Control placeholder={imageFile?.name} disabled />
                        {clipboardUI(imageFile?.name)}
                      </div>
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>URL</Form.Label>
                      <div className='d-flex'>
                        <Form.Control placeholder={imageFile?.url} disabled />
                        {clipboardUI(imageFile?.url)}
                      </div>
                    </Form.Group>
                  </Form>
                  <div style={{ maxHeight: '30rem' }}>
                    <h3>Logs</h3>
                    <Table striped bordered hover>
                      <thead>
                        <tr>
                          <th>User</th>
                          <th>Action</th>
                          <th>Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {imageFile?.logs?.map((log, index) => {
                          const date = new Date(log.time);
                          return (
                            <tr key={index}>
                              <td>{log.user}</td>
                              <td>{log.action}</td>
                              <td>{`${date.toLocaleTimeString()} ${date.toLocaleDateString()}`}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                </div>
              </div>
              <div className='d-flex bg-light align-items-center justify-content-between px-5 py-3'>
                <div
                  className='d-flex bg-primary px-3 py-2 rounded'
                  style={{ gap: '1rem' }}>
                  <OverlayTrigger
                    overlay={
                      <Tooltip id='bytesToMb'>
                        <h4 className='p-1'>{`${convertBytesToMB(
                          parseInt(imageFile?.size)
                        ).toPrecision(3)} MB`}</h4>
                      </Tooltip>
                    }>
                    <p className='m-0'>
                      File Size: {`${imageFile?.size} bytes`}
                    </p>
                  </OverlayTrigger>
                  {imageFile?.dimension ? (
                    <p className='m-0'>Dimension: {imageFile?.dimension}</p>
                  ) : null}
                </div>

                <div className='d-flex' style={{ gap: '1rem' }}>
                  <Button
                    variant='outline-danger'
                    style={{ fontSize: '1.8rem', width: '10rem' }}
                    onClick={() => handleEditClose(false)}>
                    Cancel
                  </Button>
                  <Button
                    variant={
                      !uploadImage || updateImageLoader
                        ? 'secondary'
                        : 'success'
                    }
                    style={{ fontSize: '1.8rem', width: '10rem' }}
                    disabled={!uploadImage || updateImageLoader}
                    onClick={() => handleSaveImage()}>
                    {!updateImageLoader ? 'Save' : 'Saving'}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </CommonModal>
    </>
  );
};

export default EditImageModal;
